import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { env } from '@/config/env';

const apiClient: AxiosInstance = axios.create({
  baseURL: `${env.api.baseUrl}/api/${env.api.version}`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('pfg_token');

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

type BackendErrorBody = {
  message?: string | string[];
  error?: string | string[];
  errors?: string[] | Record<string, string | string[]>;
  details?: string | string[] | Record<string, string | string[]>;
};

function extractBackendMessage(data: BackendErrorBody | undefined): string | undefined {
  if (!data) return undefined;

  const flatten = (v: unknown): string[] => {
    if (!v) return [];
    if (typeof v === 'string') return [v];
    if (Array.isArray(v)) return v.flatMap(flatten);
    if (typeof v === 'object') return Object.values(v as Record<string, unknown>).flatMap(flatten);
    return [];
  };

  const detailMessages = [
    ...flatten(data.details),
    ...flatten(data.errors),
    ...(Array.isArray(data.message) ? data.message : []),
  ].filter(Boolean);

  if (detailMessages.length > 0) return detailMessages.join(', ');

  if (typeof data.message === 'string' && data.message !== 'Bad Request Exception') {
    return data.message;
  }
  if (typeof data.error === 'string') return data.error;
  if (typeof data.message === 'string') return data.message;
  return undefined;
}

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<BackendErrorBody>) => {
    const isLoginRequest = error.config?.url?.includes('/auth/login');

    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('pfg_token');
      window.location.href = '/login';
    }

    const backendMessage = extractBackendMessage(error.response?.data);
    return Promise.reject(backendMessage ? new Error(backendMessage) : error);
  }
);

export default apiClient;
